import { useState, useEffect, useMemo, useCallback } from "react";
import Api from "../Auth/Api";
import { pdf } from "@react-pdf/renderer";
import GerarHistoricoEscolar from "../Components/GerarHistoricoEscolar";
import TableHistoricoEscolar from "../Components/TableHistoricoEscolar";
import { Modal, Badge } from "react-bootstrap";
import Tippy from "@tippyjs/react";
import Select from "react-select";
import { customStyles } from "../utils/selectStyles";
import { DEFAULT_LISTA_TIPO, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "../utils/paginationDefaults";

function HistoricoEscolar() {

    const token = JSON.parse(localStorage.getItem("user_token"));
    const [selectedTipo, setSelectedTipo] = useState(DEFAULT_LISTA_TIPO);
    const [loading, setLoading] = useState(true);

    const filterTipo = useMemo(() => [
        { value: 'todos', label: 'Todos' },
        { value: 'favoritos', label: 'Favoritos' },
    ], [])

    //listar todos os cursos
    const [dataTopicos, setDataTopicos] = useState([]);
    useEffect(() => {
        async function fetchData() {
            try {
                const response = await Api.get(`/api/listar-topicos`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataTopicos(response.data);
            } catch (error) {
                console.error(error.response.data.message)
            }
        }
        fetchData();
    }, [token])

    // listar histórico escolar
    const [dataHistoricoEscolar, setDataHistoricoEscolar] = useState([]);
    useEffect(() => {
        setLoading(true);
        async function fetchData() {
            try {
                const response = await Api.get(`/api/listar-historico-escolar/${selectedTipo}`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
                setDataHistoricoEscolar(response.data);
            } catch (error) {
                setDataHistoricoEscolar([]);
                console.error(error.response.data.message)
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [token, selectedTipo])

    // Filtro para seleção por curso
    const [selectTopico, setSelectTopico] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    
    // Filtro por busca
    const FilterDataBusca = useMemo(() => {
        if (!globalFilter) return dataHistoricoEscolar;
        const filterLower = globalFilter.toLowerCase();
        return dataHistoricoEscolar.filter(item => 
            item.nomeAluno?.toLowerCase().includes(filterLower) ||
            item.nomeTopico?.toLowerCase().includes(filterLower)
        );
    }, [dataHistoricoEscolar, globalFilter]);
    
    const FilterDataProvas = selectTopico ? FilterDataBusca.filter(item => item.idTopico === selectTopico) : FilterDataBusca;
    
    // Labels para chips
    const topicoSelecionadoNome = useMemo(() => {
        if (!selectTopico) return null;
        const topico = dataTopicos.find(t => t.id === selectTopico);
        return topico ? topico.nome : null;
    }, [selectTopico, dataTopicos]);
    
    const tipoSelecionadoLabel = useMemo(() => {
        if (!selectedTipo || selectedTipo === DEFAULT_LISTA_TIPO) return null;
        const tipo = filterTipo.find(t => t.value === selectedTipo);
        return tipo ? tipo.label : null;
    }, [selectedTipo, filterTipo]);

    // Gerar PDF
    const loadPDFData = useCallback(async (data) => {
        try {
            // Buscar os dados para gerar o PDF
            const response = await Api.get(`api/info-historico-escolar/${data.idUsuario}/${data.idTopico}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Gerar o PDF e obter como Blob
            const blob = await pdf(<GerarHistoricoEscolar data={response.data} />).toBlob();

            // Criar uma URL para o Blob e abrir em uma nova aba
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');  // Abre o PDF em uma nova guia
        } catch (error) {
            console.error(error.response.data.message);
        }
    }, [token]);

    const [showModalDetalhes, setShowModalDetalhes] = useState(false);
    const [historicoSelecionado, setHistoricoSelecionado] = useState(null);
    const [detalhesHistorico, setDetalhesHistorico] = useState(null);

    const carregarDetalhesHistorico = useCallback(async (idUsuario, idTopico) => {
        try {
            const response = await Api.get(`api/info-historico-escolar/${idUsuario}/${idTopico}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDetalhesHistorico(response.data);
            setShowModalDetalhes(true);
        } catch (error) {
            console.error(error.response.data.message);
        }
    }, [token]);

    const columns = useMemo(
        () => [
            {
                Header: () => null, // Sem título para esta coluna
                id: 'foto',
                accessor: row => row.foto,
                disableSortBy: true,
                Cell: ({ cell: { value }, row: { original } }) => (
                    <div className="d-flex align-items-center justify-content-center">
                        {value ? <img className="object-fit-cover rounded-5" src={`${process.env.REACT_APP_API_URL}/fotos/${value}`} alt="Profile" style={{ height: '42px', width: '42px' }} /> : <i className="bi bi-person-circle fs-2" />}
                    </div>
                )
            },
            {
                Header: 'Aluno',
                accessor: 'nomeAluno',
                disableSortBy: false
            },
            {
                Header: 'Topico',
                accessor: 'nomeTopico',
                disableSortBy: false
            },
            {
                Header: () => null,
                id: 'acoes',
                accessor: row => row.id,
                disableSortBy: true,
                Cell: ({ cell: { value }, row: { original } }) => (
                    <div className="d-flex align-items-center justify-content-center gap-2">
                        <Tippy content="Baixar Histórico">
                            <button
                                onClick={() => loadPDFData({
                                    idUsuario: original.idAluno,
                                    idTopico: original.idTopico,
                                    topico: original.nomeTopico
                                })}
                                className="btn btn-outline-primary text-primary rounded px-2"
                            > 
                                <i className="bi bi-box-arrow-in-down"></i>
                            </button>
                        </Tippy>
                        <Tippy content="Ver Detalhes">
                            <button
                                onClick={() => {
                                    setHistoricoSelecionado(original);
                                    carregarDetalhesHistorico(original.idAluno, original.idTopico);
                                }}
                                className="btn btn-outline-primary text-primary rounded px-2"
                            >
                                <i className="bi bi-eye"></i>
                            </button>
                        </Tippy>
                    </div>
                )
            }
        ],
        [loadPDFData, carregarDetalhesHistorico]
    )

    const LoadingScreen = () => (
        <div className="d-flex align-items-center justify-content-center p-5">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
            </div>
        </div>
    );

    return (
        <>
            <div className="fade-in w-100 mt-3" style={{ marginBottom: '100px' }}>
                {/* Header */}
                <div className="rounded-3 mb-4">
                    <div className="mb-3">
                        <h1 className="text-primary fs-2 fw-bold mb-2">Histórico Escolar</h1>
                        <p className="text-muted mb-0">Visualize e gerencie os históricos escolares dos alunos por tópico e tipo de filtro</p>
                    </div>
                </div>

                {/* Barra de Filtros Compacta */}
                <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body p-3">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-5">
                                <label className="form-label text-primary fw-medium small mb-1">Buscar</label>
                                <div className="inner-addon right-addon">
                                    <i className="d-flex bi bi-search" />
                                    <input
                                        type="search"
                                        value={globalFilter || ""}
                                        onChange={e => setGlobalFilter(e.target.value)}
                                        placeholder="Nome do aluno ou tópico..."
                                        className="form-control py-2"
                                    />
                                </div>
                            </div>
                            <div className="col-md-3">
                                <label className="form-label text-primary fw-medium small mb-1">Tópico</label>
                                <Select
                                    options={dataTopicos.map((value) => ({
                                        value: value.id,
                                        label: value.nome,
                                    }))}
                                    styles={customStyles}
                                    placeholder="Selecione o tópico"
                                    value={selectTopico ? { value: selectTopico, label: dataTopicos.find(t => t.id === selectTopico)?.nome } : null}
                                    onChange={(selectedOption) => {
                                        setSelectTopico(selectedOption ? selectedOption.value : null);
                                    }}
                                    isClearable
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label text-primary fw-medium small mb-1">Tipo</label>
                                <Select
                                    options={filterTipo.map((value) => ({
                                        value: value.value,
                                        label: value.label
                                    }))}
                                    styles={customStyles}
                                    placeholder="Selecione o tipo"
                                    value={filterTipo.find(opt => opt.value === selectedTipo) ? { value: selectedTipo, label: filterTipo.find(opt => opt.value === selectedTipo).label } : null}
                                    onChange={(selectedOption) => {
                                        setSelectedTipo(selectedOption ? selectedOption.value : DEFAULT_LISTA_TIPO);
                                    }}
                                />
                            </div>
                            <div className="col-md-2">
                                <label className="form-label text-primary fw-medium small mb-1">Itens por página</label>
                                <select 
                                    className="form-select py-2" 
                                    value={pageSize} 
                                    onChange={e => { setPageSize(Number(e.target.value)) }}
                                >
                                    {[...PAGE_SIZE_OPTIONS].map(size => (
                                        <option key={size} value={size}>
                                            {size}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Chips de Filtros Ativos */}
                {(topicoSelecionadoNome || tipoSelecionadoLabel) && (
                    <div className="mb-3 d-flex flex-wrap gap-2 align-items-center">
                        {topicoSelecionadoNome && (
                            <Badge bg="primary" className="d-flex align-items-center gap-1 px-3 py-2" style={{ fontSize: '0.875rem' }}>
                                Tópico: {topicoSelecionadoNome}
                                <i 
                                    className="bi bi-x cursor-pointer" 
                                    onClick={() => setSelectTopico(null)}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </Badge>
                        )}
                        {tipoSelecionadoLabel && (
                            <Badge bg="success" className="d-flex align-items-center gap-1 px-3 py-2" style={{ fontSize: '0.875rem' }}>
                                Tipo: {tipoSelecionadoLabel}
                                <i 
                                    className="bi bi-x cursor-pointer" 
                                    onClick={() => setSelectedTipo(DEFAULT_LISTA_TIPO)}
                                    style={{ cursor: 'pointer' }}
                                ></i>
                            </Badge>
                        )}
                    </div>
                )}

                <div className={loading ? "" : "d-none"}>
                    <LoadingScreen />
                </div>
                <div className={loading ? "d-none" : ""}>
                    <TableHistoricoEscolar 
                        columns={columns} 
                        data={FilterDataProvas} 
                        filterSelect={[]} 
                        setSelectTopico={setSelectTopico} 
                        filterTipo={[]} 
                        setSelectedTipo={setSelectedTipo} 
                        titulo="Lista de histórico escolar"
                        search={false}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        showPageSize={false}
                    />
                </div>
            </div>

            <Modal size="lg" show={showModalDetalhes} onHide={() => setShowModalDetalhes(false)}>
                <Modal.Header closeButton>
                    <span className="fw-semibold fs-4 text-primary">Detalhes do Histórico Escolar</span>
                </Modal.Header>
                <Modal.Body>
                    {detalhesHistorico && (
                        <div className="container">
                            <div className="row">
                                <div className="col-md-12">
                                    <div className="card border-0 shadow-sm">
                                        <div className="card-body">
                                            <div className="mb-4">
                                                <h5 className="text-primary">Informações do Aluno</h5>
                                                <p className="mb-1"><strong>Nome:</strong> {detalhesHistorico.info_usuario?.nome_completo || 'N/A'}</p>
                                                <p className="mb-1"><strong>Código:</strong> {detalhesHistorico.info_usuario?.codigo || 'N/A'}</p>
                                                <p className="mb-1"><strong>Unidade:</strong> {detalhesHistorico.info_usuario?.unidade || 'N/A'}</p>
                                            </div>

                                            {detalhesHistorico.topico && detalhesHistorico.topico.map(topic => (
                                                <div key={topic.idTopico} className="mb-4">
                                                    <h5 className="text-primary">Tópico: {topic.nomeTopico}</h5>
                                                    
                                                    {topic.cursos && topic.cursos.map(course => (
                                                        <div key={course.idCurso} className="mb-3">
                                                            <div className="card bg-light">
                                                                <div className="card-header">
                                                                    <h6 className="mb-0">{course.nomeCurso}</h6>
                                                                </div>
                                                                <div className="card-body">
                                                                    <div className="table-responsive">
                                                                        <table className="table table-sm">
                                                                            <thead>
                                                                                <tr>
                                                                                    <th>Módulo</th>
                                                                                    <th className="text-center">Carga Horária</th>
                                                                                    <th className="text-center">Nota Final</th>
                                                                                    <th className="text-center">Situação</th>
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {course.modulos && course.modulos.map(modulo => (
                                                                                    <tr key={modulo.idModulo}>
                                                                                        <td className="small">{modulo.nomeModulo}</td>
                                                                                        <td className="text-center small">{modulo.cargaHoraria.split(':')[0] || '-'}</td>
                                                                                        <td className="text-center small">{modulo.nota !== null ? modulo.nota : "CUR"}</td>
                                                                                        <td className="text-center small">
                                                                                            <span className={`badge ${modulo.situacao === 'Aprovado' ? 'bg-success' : 'bg-danger'}`}>
                                                                                                {modulo.situacao}
                                                                                            </span>
                                                                                        </td>
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                    
                                                                    {/* Tabela para mostrar todas as avaliações detalhadas */}
                                                                    <div className="mt-3">
                                                                        <h6 className="text-primary">Detalhes das Avaliações</h6>
                                                                        <div className="table-responsive">
                                                                            <table className="table table-sm table-hover">
                                                                                <thead className="table-light">
                                                                                    <tr>
                                                                                        <th>Módulo/Avaliação</th>
                                                                                        <th className="text-center">Tipo</th>
                                                                                        <th className="text-center">Nota</th>
                                                                                        <th className="text-center">Data</th>
                                                                                        <th className="text-center">Situação</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {course.modulos && course.modulos.map(modulo => (
                                                                                        <>
                                                                                            {/* Avaliações principais */}
                                                                                            {modulo.avaliacoes && modulo.avaliacoes.map((avaliacao, idx) => (
                                                                                                <tr key={`av-${modulo.idModulo}-${idx}`}>
                                                                                                    <td className="small">
                                                                                                        {modulo.nomeModulo}
                                                                                                    </td>
                                                                                                    <td className="text-center small">Principal</td>
                                                                                                    <td className="text-center fw-bold small">{avaliacao.nota !== null ? avaliacao.nota : "N/A"}</td>
                                                                                                    <td className="text-center small">{avaliacao.data_formatada || 'N/A'}</td>
                                                                                                    <td className="text-center small">
                                                                                                        <span className={`badge ${avaliacao.situacao === 'Aprovado' ? 'bg-success' : 'bg-danger'}`}>
                                                                                                            {avaliacao.situacao}
                                                                                                        </span>
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ))}
                                                                                            
                                                                                            {/* Avaliações extras */}
                                                                                            {modulo.avaliacoes_extras && modulo.avaliacoes_extras.map((avaliacao, idx) => (
                                                                                                <tr key={`extra-${modulo.idModulo}-${idx}`}>
                                                                                                    <td>
                                                                                                        {avaliacao.nomeAvaliacaoExtra}
                                                                                                        {avaliacao.arquivado === 1 && <span className="badge bg-warning ms-2 small">Renovação</span>}
                                                                                                    </td>
                                                                                                    <td className="text-center">Extra</td>
                                                                                                    <td className="text-center fw-bold small">{avaliacao.nota !== null ? avaliacao.nota : "N/A"}</td>
                                                                                                    <td className="text-center small">{avaliacao.data_formatada || 'N/A'}</td>
                                                                                                    <td className="text-center">
                                                                                                        <span className={`badge ${avaliacao.situacao === 'Aprovado' ? 'bg-success' : 'bg-danger'}`}>
                                                                                                            {avaliacao.situacao}
                                                                                                        </span>
                                                                                                    </td>
                                                                                                </tr>
                                                                                            ))}
                                                                                        </>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <div className="d-flex justify-content-center gap-2">
                        <button 
                            className="btn btn-primary text-white px-4"
                            onClick={() => loadPDFData({
                                idUsuario: historicoSelecionado.idAluno,
                                idTopico: historicoSelecionado.idTopico,
                                topico: historicoSelecionado.nomeTopico
                            })}
                        >
                            Baixar Histórico
                        </button>
                        <button 
                            className="btn btn-outline-primary"
                            onClick={() => setShowModalDetalhes(false)}
                        >
                            Fechar
                        </button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export default HistoricoEscolar;